import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router";

import { PasswordField } from "@/features/identity/components/PasswordField";
import { useRegister } from "@/features/identity/hooks/useRegister";
import { registerSchema, type RegisterFormValues } from "@/features/identity/schemas/auth.schemas";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { showToast } from "@/components/ui/toast";
import { GUEST_ONLY_ROUTES } from "@/constants/routes";
import { ApiError } from "@/types/api";

export function RegisterPage() {
  const navigate = useNavigate();
  const registerMutation = useRegister();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: { name: "", email: "", phone: "", password: "", confirmPassword: "" },
  });

  function onSubmit(values: RegisterFormValues) {
    registerMutation.mutate(values, {
      onSuccess: (result) => {
        navigate(GUEST_ONLY_ROUTES.otpVerification, { state: { email: result.email } });
      },
      onError: (error) => {
        showToast.error(error instanceof ApiError ? error.message : "Registration failed.");
      },
    });
  }

  return (
    <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center px-6 py-12">
      <div className="w-full max-w-[400px] space-y-6">
        <div className="space-y-1 text-center">
          <h1 className="text-h1 text-foreground">Create your account</h1>
          <p className="text-body text-muted-foreground">Buy and sell locally on BazaarX.</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="name">Full name</Label>
            <Input id="name" autoComplete="name" {...register("name")} />
            {errors.name && (
              <p className="text-caption text-destructive" role="alert">
                {errors.name.message}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" autoComplete="email" {...register("email")} />
            {errors.email && (
              <p className="text-caption text-destructive" role="alert">
                {errors.email.message}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="phone">Phone</Label>
            <Input id="phone" type="tel" autoComplete="tel" {...register("phone")} />
            {errors.phone && (
              <p className="text-caption text-destructive" role="alert">
                {errors.phone.message}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <PasswordField id="password" autoComplete="new-password" {...register("password")} />
            {errors.password && (
              <p className="text-caption text-destructive" role="alert">
                {errors.password.message}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="confirmPassword">Confirm password</Label>
            <PasswordField
              id="confirmPassword"
              autoComplete="new-password"
              {...register("confirmPassword")}
            />
            {errors.confirmPassword && (
              <p className="text-caption text-destructive" role="alert">
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          <Controller
            name="acceptTerms"
            control={control}
            render={({ field }) => (
              <div className="space-y-1.5">
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="acceptTerms"
                    checked={field.value ?? false}
                    onCheckedChange={(checked) => field.onChange(checked === true)}
                  />
                  <Label htmlFor="acceptTerms" className="font-normal">
                    I agree to the Terms of Service and Privacy Policy
                  </Label>
                </div>
                {errors.acceptTerms && (
                  <p className="text-caption text-destructive" role="alert">
                    {errors.acceptTerms.message}
                  </p>
                )}
              </div>
            )}
          />

          <Button type="submit" fullWidth isLoading={registerMutation.isPending}>
            Create account
          </Button>
        </form>

        <p className="text-center text-body-sm text-muted-foreground">
          Already have an account?{" "}
          <Link
            to={GUEST_ONLY_ROUTES.login}
            className="text-primary underline-offset-4 hover:underline"
          >
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
}
